import React, { useState } from "react";
import styled from "@emotion/styled";
import { useTheme } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { useSelector } from "react-redux";
import WifiTetheringOffIcon from '@mui/icons-material/WifiTetheringOff';
import MonthlyAllChart from "./components/Charts/MonthlyAllChart";
import MonthlyTwoYearChart from "./components/Charts/MonthlyTwoYearChart";
import MonthlyOneYearChart from "./components/Charts/MonthlyOneYearChart";
import ChartToggle from "./components/ChartToggle";
import ParamtersAllChart from "./components/Charts/ParamtersAllChart";
import { selectDiscrepnacyParametersAll, selectDiscrepnacyParametersDismissals, selectDiscrepnacyParametersRefinances, selectDiscrepnacyParametersRenegotiations } from "../../../../../../slices/teamSweepParametersSlice";
import ParamtersRenegotiationsChart from "./components/Charts/ParametersRenegotiationsChart";
import ParametersRefinancesChart from "./components/Charts/ParametersRefinanceChart";
import ParamtersDismissalsChart from "./components/Charts/ParametersDismissalsChart";
import { selectTotalSuccessfulQueries } from "../../../../../../slices/teamStatsSlice";

const MainChart = ({ leadGenerationData }) => {
  const theme = useTheme()

  const teamDiscrepancyParametersAll = useSelector(selectDiscrepnacyParametersAll)
  const teamDiscrepancyParametersRenegotiations = useSelector(selectDiscrepnacyParametersRenegotiations)
  const teamDiscrepancyParametersRefinances = useSelector(selectDiscrepnacyParametersRefinances)
  const teamDiscrepancyParametersDismissals = useSelector(selectDiscrepnacyParametersDismissals)

  const [chartTimeline, setChartTimeline] = useState('All'); 
  const handleTimelineChange = (event) => {
    setChartTimeline(event.target.value)
  }
  const [scatterDisplay, setScatterDisplay] = useState('All Closures'); 
  const handleScatterDisplayChange = (event) => {
    setScatterDisplay(event.target.value)
  }
  const [chartPerspective, setChartPerspective] = useState('Monthly Stats'); 
  const handleChartPerspectiveChange = (event) => {
    setChartPerspective(event.target.value)
    setChartTimeline('All')
  }

  const totalSuccessfulQueries = useSelector(selectTotalSuccessfulQueries)

  return (
    <ChartSurface>
      <ChartHeader>
        <div>
          <SectionEyebrow>Trend Analysis</SectionEyebrow>
          <SectionTitle>
            {chartPerspective === 'Monthly Stats' ?
              `Monthly Stats`
            : chartPerspective === 'Parameter Stats' ?
              `Parameter Stats`
            :
              ''
            }
          </SectionTitle>
        </div>
        <ChartToggle
          handleTimelineChange={handleTimelineChange}
          chartTimeline={chartTimeline}
          handleChartPerspectiveChange={handleChartPerspectiveChange}
          handleScatterDisplayChange={handleScatterDisplayChange}
          chartPerspective={chartPerspective}
          scatterDisplay={scatterDisplay}
        />
      </ChartHeader>
      <ChartRegion>
        {leadGenerationData !== undefined ?
          <>
            {totalSuccessfulQueries === 0 ?
              <EmptyState>
                <WifiTetheringOffIcon />
                <EmptyTitle>no record scans completed</EmptyTitle>
              </EmptyState>
            : chartPerspective === 'Monthly Stats' && chartTimeline === 'All' ?
              <MonthlyAllChart monthlyStatsAll={leadGenerationData.all} />
            : chartPerspective === 'Monthly Stats' && chartTimeline === '2 Year' ?
              <MonthlyTwoYearChart monthlyStatsTwoYear={leadGenerationData.twoYear} />
            : chartPerspective === 'Monthly Stats' && chartTimeline === '1 Year' ?
              <MonthlyOneYearChart monthlyStatsYear={leadGenerationData.oneYear} />
            : chartPerspective === 'Parameter Stats' && scatterDisplay === 'All Closures' ?
              <ParamtersAllChart data={teamDiscrepancyParametersAll} />
            : chartPerspective === 'Parameter Stats' && scatterDisplay === 'Renegotiations' ?
              <ParamtersRenegotiationsChart data={teamDiscrepancyParametersRenegotiations} />
            : chartPerspective === 'Parameter Stats' && scatterDisplay === 'Refinances' ?
              <ParametersRefinancesChart data={teamDiscrepancyParametersRefinances} />
            : chartPerspective === 'Parameter Stats' && scatterDisplay === 'Dismissals' ?
              <ParamtersDismissalsChart data={teamDiscrepancyParametersDismissals} />
            :
              <></>
            }
          </>
        :
          <LoadingState>
            <CircularProgress
              sx={{ color: `${theme.palette.primary.main}`}}
              size={48}
            />
          </LoadingState>
        }
      </ChartRegion>
    </ChartSurface>
  )
}

const ChartSurface = styled.section(({ theme }) => `
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(20rem, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
`)

const ChartHeader = styled.header(({ theme }) => `
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(12rem, 1fr) auto;
  gap: 0.75rem;
  align-items: end;
  padding: 0.7rem 0.85rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const SectionEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.12rem;
  color: ${theme.palette.primary.main};
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const SectionTitle = styled.h2(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 0.96rem;
  font-weight: 850;
  line-height: 1.2;
`)

const ChartRegion = styled.div(({ theme }) => `
  min-width: 0;
  min-height: 0;
  height: 100%;
  padding: 0.7rem 0.8rem 0.6rem;
  background: ${theme.palette.common.white};
`)

const EmptyState = styled.div(({ theme }) => `
  min-width: 0;
  height: 100%;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 0.5rem;
  color: ${theme.palette.common.grayTextLight};

  svg {
    width: 7rem;
    height: 7rem;
    color: ${theme.palette.common.grayBorderLight};
  }
`)

const EmptyTitle = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayTextLight};
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.2;
`)

const LoadingState = styled.div(`
  min-width: 0;
  height: 100%;
  display: grid;
  place-items: center;
`)

export default MainChart;
